/**
 * App ER diagram (ADR-0010 — Dati group, slice ER-1).
 *
 * A ReactFlow canvas that draws the app's data model: one card per **table**
 * (listing its user fields, typed glyphs), **edges** for every relation /
 * relation_many column (table → target table), and the app's **datasets** as
 * dashed cards linked to their base table. Read-only in this slice (pan / zoom /
 * drag-to-rearrange locally); ER-2 adds drag-to-create-relations + a persisted
 * per-app layout.
 *
 * Field metadata + the relation target resolver are reused from `DataGrid`
 * (`fieldKindForColumn` / `fieldKindIcon` / `columnTarget`) so the glyphs match
 * the grid exactly.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, Link2, Pencil, Plus, Table2, Trash2, Wand2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
} from "reactflow";
import "reactflow/dist/style.css";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import { AddColumnDialog } from "@/components/apps/TableDesigner";
import {
  type FieldKind,
  columnTarget,
  fieldKindFor,
  fieldKindForColumn,
  fieldKindIcon,
} from "@/components/apps/DataGrid";
import { appsApi, type ColumnIn, type ColumnOut, type TableDetail, type TableSummary } from "@/lib/api-apps";
import { appEditFromConfig, isExprSelect } from "@/lib/datasets/appModelGraph";

/** First free `tabella_N` name (mirrors AppTablesPane — new ER tables auto-name,
 *  then the user renames inline). */
function nextTableName(tables: TableSummary[]): string {
  const names = new Set(tables.map((t) => t.name));
  for (let n = 1; ; n += 1) {
    const candidate = `tabella_${n}`;
    if (!names.has(candidate)) return candidate;
  }
}

/** System stamp columns — hidden in the ER (they're on every table). */
const SYSTEM_COLUMNS = new Set([
  "id",
  "created_at",
  "updated_at",
  "created_by",
  "updated_by",
  "deleted_at",
  "deleted_by",
]);

const NODE_W = 230;
const HEADER_H = 34;
const FIELD_H = 24;
const GAP_X = 170;
const GAP_Y = 130;

/** Edge stroke — a medium grey that reads on both the dark and light canvas.
 *  NB: the real CSS token is `--muted-fg` ( `muted-foreground` is the Tailwind
 *  color NAME, not a CSS var) — using the wrong one makes the stroke invalid →
 *  invisible lines. */
const EDGE_STROKE = "hsl(var(--muted-fg))";
/** Invisible anchor handles (read-only ER): they position edges but don't show. */
const HANDLE_CLS = "!h-2 !w-2 !min-w-0 !border-0 !bg-transparent";
/** Visible, grabbable anchor (edit mode) — an accent dot you drag to wire a
 *  relation. */
const HANDLE_ON = "!h-2.5 !w-2.5 !rounded-full !border-2 !border-background !bg-accent";

interface TableField {
  id: string;
  name: string;
  kind: FieldKind;
  rel: boolean;
}
interface TableNodeData {
  tableId: string;
  name: string;
  fields: TableField[];
  /** inline-designer hooks (admin only); undefined ⇒ read-only card */
  canWrite: boolean;
  onAddField: (tableId: string) => void;
  onRenameField: (tableId: string, columnId: string, current: string) => void;
  onDeleteField: (tableId: string, columnId: string, name: string) => void;
  onRenameTable: (tableId: string, current: string) => void;
  onDeleteTable: (tableId: string, name: string) => void;
}
interface DatasetNodeData {
  name: string;
  fields: { key: string; kind: FieldKind }[];
}

/** A table card: header + a typed row per user field. The left/right handles are
 *  the relation anchor points (used live in ER-2). */
function TableNode({ data, isConnectable }: NodeProps<TableNodeData>) {
  // When editing (admin), the side anchors become visible accent dots you can
  // drag from / to to wire a relation; read-only, they're invisible.
  const hc = isConnectable ? HANDLE_ON : HANDLE_CLS;
  const headerMid = HEADER_H / 2;
  // Vertical centre of field row `i`, used to anchor a relation edge at the row
  // of the FK field (classic ER look) instead of the card's mid-side.
  const rowMid = (i: number) => HEADER_H + i * FIELD_H + FIELD_H / 2;
  return (
    <div className="rounded-md border border-border bg-surface shadow-sm" style={{ width: NODE_W }}>
      {/* Connect anchors (edit mode): drag from a source dot to a target dot to
          wire a relation. Visible accent dots when admin, invisible otherwise. */}
      <Handle id="c-tl" type="target" position={Position.Left} className={hc} isConnectable={isConnectable} style={{ top: headerMid }} />
      <Handle id="c-sl" type="source" position={Position.Left} className={hc} isConnectable={isConnectable} style={{ top: headerMid }} />
      <Handle id="c-tr" type="target" position={Position.Right} className={hc} isConnectable={isConnectable} style={{ top: headerMid }} />
      <Handle id="c-sr" type="source" position={Position.Right} className={hc} isConnectable={isConnectable} style={{ top: headerMid }} />
      {/* Header-level TARGET anchors (render-only): incoming relations point here,
          on whichever side faces the source table. */}
      <Handle id="t:l" type="target" position={Position.Left} className={HANDLE_CLS} isConnectable={false} style={{ top: headerMid }} />
      <Handle id="t:r" type="target" position={Position.Right} className={HANDLE_CLS} isConnectable={false} style={{ top: headerMid }} />
      {/* Per-field SOURCE anchors (render-only): one pair per relation field, at
          that field's row, so each edge leaves from its own point (no stacking). */}
      {data.fields.map((f, i) =>
        f.rel ? (
          <React.Fragment key={`h-${f.id}`}>
            <Handle id={`s:${f.id}:l`} type="source" position={Position.Left} className={HANDLE_CLS} isConnectable={false} style={{ top: rowMid(i) }} />
            <Handle id={`s:${f.id}:r`} type="source" position={Position.Right} className={HANDLE_CLS} isConnectable={false} style={{ top: rowMid(i) }} />
          </React.Fragment>
        ) : null,
      )}
      <div className="group/hdr flex items-center gap-1.5 rounded-t-md border-b border-border bg-elevated px-2.5 py-1.5 text-sm font-semibold">
        <Table2 className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="truncate">{data.name}</span>
        {data.canWrite && (
          <span className="ml-auto hidden shrink-0 items-center gap-0.5 group-hover/hdr:flex">
            <IconBtn title="Rinomina tabella" onClick={() => data.onRenameTable(data.tableId, data.name)}>
              <Pencil className="h-3 w-3" strokeWidth={1.75} />
            </IconBtn>
            <IconBtn title="Elimina tabella" danger onClick={() => data.onDeleteTable(data.tableId, data.name)}>
              <Trash2 className="h-3 w-3" strokeWidth={1.75} />
            </IconBtn>
          </span>
        )}
      </div>
      <div className="divide-y divide-border/40">
        {data.fields.length === 0 ? (
          <div className="px-2.5 py-1 text-xs text-muted-foreground">—</div>
        ) : (
          data.fields.map((f) => {
            const Icon = fieldKindIcon(f.kind);
            return (
              <div
                key={f.id}
                className="group/row flex items-center gap-1.5 px-2.5 text-xs"
                style={{ height: FIELD_H }}
              >
                <Icon className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                <span className="truncate">{f.name}</span>
                <span className="ml-auto flex shrink-0 items-center gap-0.5">
                  {f.rel && <Link2 className="h-3 w-3 text-accent group-hover/row:hidden" strokeWidth={1.5} />}
                  {data.canWrite && (
                    <span className="hidden items-center gap-0.5 group-hover/row:flex">
                      <IconBtn title="Rinomina campo" onClick={() => data.onRenameField(data.tableId, f.id, f.name)}>
                        <Pencil className="h-2.5 w-2.5" strokeWidth={1.75} />
                      </IconBtn>
                      <IconBtn title="Elimina campo" danger onClick={() => data.onDeleteField(data.tableId, f.id, f.name)}>
                        <Trash2 className="h-2.5 w-2.5" strokeWidth={1.75} />
                      </IconBtn>
                    </span>
                  )}
                </span>
              </div>
            );
          })
        )}
      </div>
      {data.canWrite && (
        <button
          type="button"
          className="nodrag flex w-full items-center gap-1.5 rounded-b-md border-t border-border/60 px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-elevated hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            data.onAddField(data.tableId);
          }}
        >
          <Plus className="h-3 w-3" strokeWidth={1.75} /> Campo
        </button>
      )}
    </div>
  );
}

/** A tiny icon button used inside ER nodes. `nodrag` stops ReactFlow from treating
 *  the click as a node-drag; stopPropagation keeps it off the canvas. */
function IconBtn({ title, danger, onClick, children }: { title: string; danger?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      className={`nodrag rounded p-0.5 ${danger ? "text-muted-foreground hover:bg-danger/10 hover:text-danger" : "text-muted-foreground hover:bg-accent/10 hover:text-accent"}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

/** A dataset card — dashed, to read as a *derived* view over a base table. Shows
 *  its projected columns (the dataset's `config.columns` keys) like a table. */
function DatasetNode({ data }: NodeProps<DatasetNodeData>) {
  return (
    <div className="rounded-md border border-dashed border-border bg-surface/70 shadow-sm" style={{ width: NODE_W }}>
      {/* Source anchors only — a dataset edge points FROM the dashed view TO its
          base table (header-level, on the side facing the table). */}
      <Handle id="ds:l" type="source" position={Position.Left} className={HANDLE_CLS} isConnectable={false} style={{ top: HEADER_H / 2 }} />
      <Handle id="ds:r" type="source" position={Position.Right} className={HANDLE_CLS} isConnectable={false} style={{ top: HEADER_H / 2 }} />
      <div className="flex items-center gap-1.5 rounded-t-md border-b border-dashed border-border bg-elevated/60 px-2.5 py-1.5 text-sm font-semibold">
        <Database className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <span className="truncate">{data.name}</span>
      </div>
      <div className="divide-y divide-border/40">
        {data.fields.length === 0 ? (
          <div className="px-2.5 py-1 text-xs text-muted-foreground">—</div>
        ) : (
          data.fields.map((f) => {
            const Icon = fieldKindIcon(f.kind);
            return (
              <div
                key={f.key}
                className="flex items-center gap-1.5 px-2.5 text-xs"
                style={{ height: FIELD_H }}
              >
                <Icon className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                <span className="truncate">{f.key}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const nodeTypes = { table: TableNode, dataset: DatasetNode };

/** Assign each table a dependency LAYER = longest chain of "references" it sits
 *  on. A relation edge `source → target` means `source` has an FK to `target`;
 *  `layer(n) = 1 + max(layer(t))` over the tables `n` references (0 for a table
 *  that references nothing). Back-edges of a cycle contribute 0 (cycle guard) so
 *  the recursion always terminates. Tables that reference others end up in a
 *  higher layer → placed further LEFT, so edges flow left→right toward the
 *  tables they point at, instead of crossing the whole canvas. */
function layersOf(
  tableIds: string[],
  pairs: { source: string; target: string }[],
): Map<string, number> {
  const out = new Map<string, string[]>(tableIds.map((id) => [id, []]));
  for (const { source, target } of pairs) {
    if (source !== target && out.has(source) && out.has(target)) out.get(source)!.push(target);
  }
  const layer = new Map<string, number>();
  const onStack = new Set<string>();
  const visit = (id: string): number => {
    const cached = layer.get(id);
    if (cached !== undefined) return cached;
    if (onStack.has(id)) return 0; // cycle back-edge → no contribution
    onStack.add(id);
    let m = 0;
    for (const t of out.get(id) ?? []) m = Math.max(m, 1 + visit(t));
    onStack.delete(id);
    layer.set(id, m);
    return m;
  };
  tableIds.forEach(visit);
  return layer;
}

/** Layered left→right layout: tables in columns by dependency depth (referencing
 *  tables left, referenced right), stacked vertically within a column. Datasets
 *  sit in a row beneath, since they're derived views, not part of the schema DAG. */
function layout(
  tables: { id: string; data: TableNodeData; height: number }[],
  datasets: { id: string; data: DatasetNodeData }[],
  pairs: { source: string; target: string }[],
): Node[] {
  const layer = layersOf(
    tables.map((t) => t.id),
    pairs,
  );
  const maxLayer = Math.max(0, ...[...layer.values()]);
  // Column index = mirror of the layer, so the deepest referencer is column 0
  // (far left) and a referenced-by-all leaf is on the right.
  const colOf = (id: string) => maxLayer - (layer.get(id) ?? 0);

  // Group tables per column, preserving input order for stable vertical stacking.
  const byCol = new Map<number, typeof tables>();
  for (const t of tables) {
    const c = colOf(t.id);
    if (!byCol.has(c)) byCol.set(c, []);
    byCol.get(c)!.push(t);
  }

  const nodes: Node[] = [];
  let maxBottom = 0;
  for (const [col, items] of byCol) {
    let y = 0;
    for (const t of items) {
      nodes.push({
        id: t.id,
        type: "table",
        position: { x: col * (NODE_W + GAP_X), y },
        data: t.data,
      });
      y += t.height + GAP_Y;
      maxBottom = Math.max(maxBottom, y);
    }
  }
  const dsY = maxBottom + GAP_Y;
  datasets.forEach((d, i) => {
    nodes.push({
      id: d.id,
      type: "dataset",
      position: { x: i * (NODE_W + GAP_X), y: dsY },
      data: d.data,
      // Datasets are read-only views — never a relation source/target.
      connectable: false,
    });
  });
  return nodes;
}

export function AppErDiagram({ appId, canWrite = false }: { appId: string; canWrite?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const tablesQ = useQuery({
    queryKey: ["apps", appId, "tables"],
    queryFn: () => appsApi.listTables(appId),
  });
  const tableList = tablesQ.data ?? [];

  // One detail fetch per table for its columns (cache-shared with the designer).
  const detailQs = useQueries({
    queries: tableList.map((tb) => ({
      queryKey: ["apps", appId, "tables", tb.id],
      queryFn: () => appsApi.getTable(appId, tb.id),
    })),
  });
  const details = detailQs
    .map((q) => q.data)
    .filter((d): d is TableDetail => Boolean(d));

  const datasetsQ = useQuery({
    queryKey: ["apps", appId, "datasets"],
    queryFn: () => appsApi.listDatasets(appId),
  });
  const datasets = datasetsQ.data ?? [];

  const loading = tablesQ.isLoading || detailQs.some((q) => q.isLoading);

  // Highlight a single relation on hover — accent the hovered edge and dim the
  // rest, so overlapping edges become readable.
  const [hoveredEdge, setHoveredEdge] = React.useState<string | null>(null);

  // ER-2: drag table→table to wire a relation. The pending connection opens a
  // dialog (field name + single/many); confirm adds a relation column on the
  // SOURCE table pointing at the target — reusing the existing add-column API.
  const nameById = React.useMemo(() => new Map(details.map((d) => [d.id, d.name])), [details]);
  const [connect, setConnect] = React.useState<Connection | null>(null);
  const addRelation = useMutation({
    mutationFn: (v: { tableId: string; body: ColumnIn }) =>
      appsApi.addColumn(appId, v.tableId, v.body),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "tables", v.tableId] });
      qc.invalidateQueries({ queryKey: ["apps", appId, "tables"] });
      setConnect(null);
    },
  });

  // ── Inline designer (owner: edit the schema without leaving the ER) ──────────
  const invalidate = React.useCallback(
    (tableId?: string) => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "tables"] });
      if (tableId) qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId] });
    },
    [qc, appId],
  );
  const [addFieldFor, setAddFieldFor] = React.useState<string | null>(null);
  const [rename, setRename] = React.useState<
    { kind: "table" | "field"; tableId: string; columnId?: string; current: string } | null
  >(null);
  const renameCol = useMutation({
    mutationFn: (v: { tableId: string; columnId: string; name: string }) =>
      appsApi.renameColumn(appId, v.tableId, v.columnId, v.name),
    onSuccess: (_d, v) => {
      invalidate(v.tableId);
      setRename(null);
    },
  });
  const dropCol = useMutation({
    mutationFn: (v: { tableId: string; columnId: string }) => appsApi.dropColumn(appId, v.tableId, v.columnId),
    onSuccess: (_d, v) => invalidate(v.tableId),
  });
  const createTbl = useMutation({
    mutationFn: () => appsApi.createTable(appId, { name: nextTableName(tableList) }),
    onSuccess: () => invalidate(),
  });
  const renameTbl = useMutation({
    mutationFn: (v: { tableId: string; name: string }) => appsApi.updateTable(appId, v.tableId, { name: v.name }),
    onSuccess: (_d, v) => {
      invalidate(v.tableId);
      setRename(null);
    },
  });
  const dropTbl = useMutation({
    mutationFn: (tableId: string) => appsApi.removeTable(appId, tableId),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["apps", appId, "datasets"] });
    },
  });

  const onAddField = React.useCallback((tableId: string) => setAddFieldFor(tableId), []);
  const onRenameField = React.useCallback(
    (tableId: string, columnId: string, current: string) => setRename({ kind: "field", tableId, columnId, current }),
    [],
  );
  const onDeleteField = React.useCallback(
    (tableId: string, columnId: string, name: string) => {
      if (window.confirm(`Eliminare il campo "${name}"? I suoi dati vanno persi.`)) dropCol.mutate({ tableId, columnId });
    },
    [dropCol],
  );
  const onRenameTable = React.useCallback(
    (tableId: string, current: string) => setRename({ kind: "table", tableId, current }),
    [],
  );
  const onDeleteTable = React.useCallback(
    (tableId: string, name: string) => {
      if (window.confirm(`Eliminare la tabella "${name}"? I suoi dati vanno persi.`)) dropTbl.mutate(tableId);
    },
    [dropTbl],
  );

  // ER-2b: saved per-app layout. The app detail (cache-shared with the page)
  // carries `er_layout.positions`; when present they OVERRIDE the auto-layout.
  // Dragging a node (admin) persists the whole position map.
  const appQ = useQuery({ queryKey: ["apps", appId], queryFn: () => appsApi.get(appId) });
  const savedPos = appQ.data?.er_layout?.positions ?? {};
  const savedKey = JSON.stringify(savedPos);
  const saveLayout = useMutation({
    mutationFn: (positions: Record<string, { x: number; y: number }>) =>
      appsApi.saveErLayout(appId, positions),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId] }),
  });

  const { nodes, edges } = React.useMemo(() => {
    const tableIds = new Set(details.map((d) => d.id));
    const tableNodes = details.map((d) => {
      const userCols = (d.columns ?? []).filter((c) => !SYSTEM_COLUMNS.has(c.name));
      const fields: TableField[] = userCols.map((c: ColumnOut) => {
        const kind = fieldKindForColumn(c);
        return { id: c.id, name: c.name, kind, rel: kind === "relation" || kind === "relation_many" };
      });
      return {
        id: d.id,
        data: {
          tableId: d.id,
          name: d.name,
          fields,
          canWrite,
          onAddField,
          onRenameField,
          onDeleteField,
          onRenameTable,
          onDeleteTable,
        },
        // The inline "+ Campo" footer (admin) adds one row's worth of height.
        height: HEADER_H + Math.max(1, fields.length) * FIELD_H + (canWrite ? FIELD_H : 0),
      };
    });
    const dsNodes = datasets.map((d) => ({
      id: d.id,
      data: {
        name: d.name,
        // Dual-read: an app projection's config is EITHER a legacy DatasetSpec
        // (`columns`) OR the unified app-flavour `config.model` (`select`) — ADR-0063
        // FE-3. `appEditFromConfig` normalises both to the projected output columns.
        fields: appEditFromConfig(d.config as Record<string, unknown>, d.base_table_id).select.map(
          (s) => ({
            key: s.as,
            kind: fieldKindFor(isExprSelect(s) ? s.result_type ?? undefined : undefined),
          }),
        ),
      },
    }));
    // Relation pairs (source table → referenced table) drive the layered layout.
    const relPairs: { source: string; target: string }[] = [];
    for (const d of details) {
      for (const c of d.columns ?? []) {
        const kind = fieldKindForColumn(c);
        if (kind !== "relation" && kind !== "relation_many") continue;
        const target = columnTarget(c);
        if (target && tableIds.has(target)) relPairs.push({ source: d.id, target });
      }
    }

    const built = layout(tableNodes, dsNodes, relPairs);
    // Saved positions override the auto-layout (per-app, ER-2b).
    for (const n of built) {
      const p = (savedPos as Record<string, { x: number; y: number }>)[n.id];
      if (p && typeof p.x === "number" && typeof p.y === "number") {
        n.position = { x: p.x, y: p.y };
      }
    }
    const posX = new Map(built.map((n) => [n.id, n.position.x]));
    // The edge leaves the source on whichever side faces the target, and enters
    // the target on its facing side — so a line never wraps around a card.
    const facingRight = (from: string, to: string) => (posX.get(from) ?? 0) <= (posX.get(to) ?? 0);

    const built_edges: Edge[] = [];
    for (const d of details) {
      for (const c of d.columns ?? []) {
        const kind = fieldKindForColumn(c);
        if (kind !== "relation" && kind !== "relation_many") continue;
        const target = columnTarget(c);
        if (!target || !tableIds.has(target)) continue;
        const right = facingRight(d.id, target);
        built_edges.push({
          id: `rel:${d.id}:${c.id}`,
          source: d.id,
          target,
          // Anchor at the FK FIELD's row (classic ER), header on the target.
          sourceHandle: `s:${c.id}:${right ? "r" : "l"}`,
          targetHandle: `t:${right ? "l" : "r"}`,
          type: "smoothstep",
          pathOptions: { borderRadius: 12 },
          // Label lives on the field row already; show it only on hover (see
          // displayEdges) to keep the canvas clean. Stash the name in `data`.
          data: { relName: c.name },
          markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: EDGE_STROKE },
          style: { stroke: EDGE_STROKE, strokeWidth: 1.5, opacity: 0.65 },
        });
      }
    }
    for (const d of datasets) {
      if (d.base_table_id && tableIds.has(d.base_table_id)) {
        const right = facingRight(d.id, d.base_table_id);
        built_edges.push({
          id: `ds:${d.id}`,
          source: d.id,
          target: d.base_table_id,
          sourceHandle: `ds:${right ? "r" : "l"}`,
          targetHandle: `t:${right ? "l" : "r"}`,
          type: "smoothstep",
          pathOptions: { borderRadius: 12 },
          animated: true,
          style: { stroke: EDGE_STROKE, strokeWidth: 1.5, strokeDasharray: "5 4", opacity: 0.5 },
          markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: EDGE_STROKE },
        });
      }
    }
    return { nodes: built, edges: built_edges };
    // details/datasets are fresh arrays each render; key off lengths + ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    details.map((d) => `${d.id}:${d.columns?.length ?? 0}`).join(","),
    datasets.map((d) => d.id).join(","),
    savedKey,
  ]);

  // Controlled nodes so drags are live; re-seed when the computed nodes change
  // (data refetch / saved-layout change). Dragging persists the whole map.
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  React.useEffect(() => {
    setRfNodes(nodes);
  }, [nodes, setRfNodes]);

  const persistLayout = () => {
    const positions = Object.fromEntries(
      rfNodes.map((n) => [n.id, { x: Math.round(n.position.x), y: Math.round(n.position.y) }]),
    );
    saveLayout.mutate(positions);
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center rounded-lg border border-border text-sm text-muted-foreground">
        {t("common.loading", "Loading…")}
      </div>
    );
  }
  if (details.length === 0) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-3 rounded-lg border border-border text-sm text-muted-foreground">
        {t("apps.erEmpty", "No tables yet — add one to see the schema diagram.")}
        {canWrite && (
          <Button size="sm" disabled={createTbl.isPending} onClick={() => createTbl.mutate()}>
            <Plus className="h-4 w-4" strokeWidth={1.5} /> {t("apps.erNewTable", "Nuova tabella")}
          </Button>
        )}
      </div>
    );
  }

  // Hover highlight: accent the hovered edge (+ its arrow/label) and fade the
  // rest, so a single relation stands out even where edges overlap.
  const HI = "hsl(var(--ring))";
  const displayEdges = hoveredEdge
    ? edges.map((e) =>
        e.id === hoveredEdge
          ? {
              // NB: do NOT bump zIndex here — re-sorting edges into a different
              // SVG layer re-parents the hovered path, so its `mouseleave` never
              // fires and the highlight gets stuck. The accent + dimmed siblings
              // already make it read as on-top. The pane-move / container-leave
              // handlers below are the belt-and-braces reset.
              ...e,
              animated: false,
              // Reveal the relation field name only on the hovered edge.
              label: (e.data as { relName?: string } | undefined)?.relName,
              style: { ...e.style, stroke: HI, strokeWidth: 2.5, opacity: 1 },
              markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: HI },
              labelStyle: { fontSize: 10, fill: HI, fontWeight: 600 },
              labelBgStyle: { fill: "hsl(var(--surface))", fillOpacity: 0.95 },
              labelBgPadding: [4, 2] as [number, number],
              labelBgBorderRadius: 4,
            }
          : { ...e, style: { ...e.style, opacity: 0.08 } },
      )
    : edges;

  const hasSavedLayout = Object.keys(savedPos as object).length > 0;

  return (
    <>
      <div
        className="relative h-[70vh] w-full overflow-hidden rounded-lg border border-border bg-background"
        // Safety net: clear the hover if the cursor leaves the whole canvas
        // (covers the case where ReactFlow's per-edge mouseleave is missed).
        onMouseLeave={() => setHoveredEdge(null)}
      >
        {/* Inline designer: create a new table right here (auto-named, then
            renamed inline). New card joins the diagram on the next refetch. */}
        {canWrite && (
          <div className="absolute left-3 top-3 z-10">
            <Button size="sm" disabled={createTbl.isPending} onClick={() => createTbl.mutate()}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("apps.erNewTable", "Nuova tabella")}
            </Button>
          </div>
        )}
        {/* Auto-layout: clear the saved per-app positions so the dependency
            layout re-applies. Only useful (and shown) once a custom layout
            exists, and only to admins who can persist it. */}
        {canWrite && hasSavedLayout && (
          <div className="absolute right-3 top-3 z-10">
            <Button
              variant="outline"
              size="sm"
              disabled={saveLayout.isPending}
              onClick={() => saveLayout.mutate({})}
            >
              <Wand2 className="h-4 w-4" strokeWidth={1.5} />
              {t("apps.erAutoLayout", "Auto-layout")}
            </Button>
          </div>
        )}
        <ReactFlowProvider>
          <ReactFlow
            nodes={rfNodes}
            edges={displayEdges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            nodesConnectable={canWrite}
            nodesDraggable={canWrite}
            minZoom={0.2}
            onEdgeMouseEnter={(_, edge) => setHoveredEdge(edge.id)}
            onEdgeMouseLeave={() => setHoveredEdge(null)}
            // Entering a node also means we're no longer on an edge — a cheap,
            // non-intrusive reset that un-sticks a missed edge-mouseleave without
            // fighting the hover the way a pane-mousemove handler would.
            onNodeMouseEnter={() => setHoveredEdge(null)}
            onConnect={(c) => {
              if (c.source && c.target) setConnect(c);
            }}
            onNodeDragStop={() => {
              if (canWrite) persistLayout();
            }}
          >
            <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      <RelationCreateDialog
        open={!!connect}
        sourceName={connect ? (nameById.get(connect.source ?? "") ?? "?") : ""}
        targetName={connect ? (nameById.get(connect.target ?? "") ?? "?") : ""}
        busy={addRelation.isPending}
        errMsg={addRelation.error ? (addRelation.error as Error).message : null}
        onCancel={() => setConnect(null)}
        onCreate={(name, many) => {
          if (!connect?.source || !connect?.target) return;
          const body: ColumnIn = many
            ? {
                name,
                data_type: "jsonb",
                required: false,
                kind: "relation_many",
                config: { target_table: connect.target },
              }
            : {
                name,
                data_type: "uuid",
                required: false,
                kind: "relation",
                config: { target_table: connect.target, on_delete: "restrict" },
              };
          addRelation.mutate({ tableId: connect.source, body });
        }}
      />

      {/* Reuse the Core's FULL add-column dialog (all types + relation/lookup/
          rollup/formula) — so adding a field never leaves the ER. */}
      {addFieldFor && (
        <AddColumnDialog appId={appId} tableId={addFieldFor} open onOpenChange={(o) => !o && setAddFieldFor(null)} />
      )}

      {/* One rename dialog for both a table and a field. */}
      <RenameDialog
        open={!!rename}
        title={rename?.kind === "table" ? t("apps.erRenameTable", "Rinomina tabella") : t("apps.erRenameField", "Rinomina campo")}
        current={rename?.current ?? ""}
        busy={renameCol.isPending || renameTbl.isPending}
        onCancel={() => setRename(null)}
        onSave={(name) => {
          if (!rename) return;
          if (rename.kind === "table") renameTbl.mutate({ tableId: rename.tableId, name });
          else if (rename.columnId) renameCol.mutate({ tableId: rename.tableId, columnId: rename.columnId, name });
        }}
      />
    </>
  );
}

/** A minimal rename dialog (name input) reused for a table or a field. */
function RenameDialog({
  open,
  title,
  current,
  busy,
  onCancel,
  onSave,
}: {
  open: boolean;
  title: string;
  current: string;
  busy: boolean;
  onCancel: () => void;
  onSave: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = React.useState(current);
  React.useEffect(() => {
    if (open) setName(current);
  }, [open, current]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="er-rename">{t("apps.name", "Nome")}</Label>
            <Input
              id="er-rename"
              value={name}
              autoFocus
              className="font-mono"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim() && !busy) onSave(name.trim());
              }}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!name.trim() || busy} onClick={() => onSave(name.trim())}>
            {busy ? t("common.saving", "Saving…") : t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The confirm dialog for a drag-created relation: name the field + choose
 *  single vs many. Seeds the field name from the target table on open. */
function RelationCreateDialog({
  open,
  sourceName,
  targetName,
  busy,
  errMsg,
  onCancel,
  onCreate,
}: {
  open: boolean;
  sourceName: string;
  targetName: string;
  busy: boolean;
  errMsg: string | null;
  onCancel: () => void;
  onCreate: (name: string, many: boolean) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = React.useState("");
  const [many, setMany] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // Default field name = the target table's name, lowercased + underscored.
      setName(targetName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""));
      setMany(false);
    }
  }, [open, targetName]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apps.erNewRelation", "New relation")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("apps.erRelationFromTo", "A field on {{from}} linking to {{to}}.", {
              from: sourceName,
              to: targetName,
            })}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="er-rel-name">{t("apps.erFieldName", "Field name")}</Label>
            <Input
              id="er-rel-name"
              value={name}
              autoFocus
              className="font-mono"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="er-rel-many">{t("apps.erAllowMany", "Link to many rows")}</Label>
            <Switch id="er-rel-many" checked={many} onCheckedChange={setMany} />
          </div>
          {errMsg && (
            <Alert tone="danger" title={t("apps.erRelationFailed", "Could not create relation")}>
              <span className="font-mono text-xs">{errMsg}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!name.trim() || busy} onClick={() => onCreate(name.trim(), many)}>
            {busy ? t("common.saving", "Saving…") : t("apps.erCreateRelation", "Create relation")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
